import { useEffect, useState } from "react";
import { Button, Select, Table, Tag } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getPatientConsents } from "../../apis/consent";
import { PatientConsent } from "../../interfaces/Consent";
import { useNotification } from "../../NotificationContext";

const STATUS_COLORS: Record<string, string> = {
  generated: "default",
  printed: "blue",
  signed_uploaded: "green",
  void: "red",
};

const PatientConsentList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showNotification } = useNotification();
  const [consents, setConsents] = useState<PatientConsent[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const visitId  = searchParams.get("visit_id");
  const patientId = searchParams.get("patient_id");

  const load = () => {
    setLoading(true);
    const params: Record<string, any> = {};
    if (statusFilter) params.status = statusFilter;
    if (visitId)      params.visit_id = visitId;
    if (patientId)    params.patient_id = patientId;
    getPatientConsents(params)
      .then((res: any) => setConsents(res.data ?? res))
      .catch(() => showNotification("error", "Failed to load consents", "", "topRight"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter, visitId, patientId]);

  const clearScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("visit_id");
    next.delete("patient_id");
    setSearchParams(next);
  };

  const columns = [
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      render: (ref: string) => <span className="font-mono text-xs text-indigo-600">{ref}</span>,
    },
    {
      title: "Patient",
      key: "patient",
      render: (_: any, c: PatientConsent) =>
        c.patient ? `${c.patient.firstName} ${c.patient.lastName}` : `#${c.patientId}`,
    },
    {
      title: "Template",
      key: "template",
      render: (_: any, c: PatientConsent) => c.template?.name ?? `#${c.templateId}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s: string) => (
        <Tag color={STATUS_COLORS[s] ?? "default"}>{s.replace(/_/g, " ")}</Tag>
      ),
    },
    {
      title: "Visit",
      dataIndex: "visitId",
      key: "visitId",
      render: (v: number | null) => (v ? `#${v}` : "—"),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (d: string) => new Date(d).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, c: PatientConsent) => (
        <Button size="small" onClick={() => navigate(`/patient-consents/${c.id}`)}>
          View
        </Button>
      ),
    },
  ];

  const scopeLabel = visitId
    ? `Visit #${visitId}`
    : patientId
    ? `Patient #${patientId}`
    : null;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">Patient Consents</h1>
          {scopeLabel && (
            <div className="flex items-center gap-2 mt-1">
              <Tag color="blue">Filtered: {scopeLabel}</Tag>
              <button
                onClick={clearScope}
                className="text-xs text-gray-400 hover:text-gray-600 underline"
              >
                Show all
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-3 mb-4">
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          style={{ width: 180 }}
          placeholder="All Statuses"
          allowClear
          options={[
            { label: "Generated", value: "generated" },
            { label: "Printed", value: "printed" },
            { label: "Signed Uploaded", value: "signed_uploaded" },
            { label: "Void", value: "void" },
          ]}
        />
      </div>

      <Table
        dataSource={consents}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
      />
    </div>
  );
};

export default PatientConsentList;
